import { useState, useEffect, useCallback } from 'react';
import { X, Activity, RefreshCw, CheckCircle2, XCircle, Clock, Loader2, Filter, Search, ChevronDown, ChevronRight, RotateCcw, Trash2, Download, BarChart3 } from 'lucide-react';
import { API_URL } from '../config';

interface Execution {
  id: string;
  flow_id: string;
  flow_name: string;
  status: 'pending' | 'running' | 'success' | 'failed' | 'cancelled' | 'timeout';
  trigger_type: string;
  started_at: string;
  finished_at?: string;
  duration_ms?: number;
  nodes_executed: number;
  error?: string;
}

interface ExecutionLog {
  timestamp: string;
  node_id: string;
  node_type: string;
  level: string;
  message: string;
  data?: any;
}

interface ExecutionStats {
  period_days: number;
  total_executions: number;
  success_count: number;
  failed_count: number;
  running_count: number;
  success_rate: number;
  avg_duration_ms: number;
  by_day: Record<string, { success: number; failed: number; total: number }>;
  by_trigger: Record<string, number>;
  top_flows: Array<{ flow_id: string; count: number; name: string }>;
}

interface ExecutionsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string;
  currentFlowId?: string;
}

export default function ExecutionsPanel({
  isOpen,
  onClose,
  userId = 'default',
  currentFlowId,
}: ExecutionsPanelProps) {
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [stats, setStats] = useState<ExecutionStats | null>(null);
  const [activeTab, setActiveTab] = useState<'list' | 'stats'>('list');
  const [loading, setLoading] = useState(false);
  const [selectedExec, setSelectedExec] = useState<string | null>(null);
  const [execLogs, setExecLogs] = useState<ExecutionLog[]>([]);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [triggerFilter, setTriggerFilter] = useState<string>('all');
  const [flowFilter, setFlowFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const loadExecutions = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/executions/list?limit=100`;
      if (userId) url += `&owner_id=${userId}`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (triggerFilter !== 'all') url += `&trigger_type=${triggerFilter}`;
      if (flowFilter !== 'all') url += `&flow_id=${flowFilter}`;
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setExecutions(data.executions || []);
      }
    } catch (e) {
      console.error('Error loading executions:', e);
    } finally {
      setLoading(false);
    }
  }, [userId, statusFilter, triggerFilter, flowFilter]);

  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/executions/stats?days=30${userId ? `&owner_id=${userId}` : ''}`);
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (e) {
      console.error('Error loading stats:', e);
    }
  }, [userId]);

  const loadLogs = async (execId: string) => {
    try {
      const res = await fetch(`${API_URL}/executions/${execId}/logs`);
      if (res.ok) {
        const data = await res.json();
        setExecLogs(data);
      }
    } catch (e) {
      console.error('Error loading logs:', e);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadExecutions();
      loadStats();
    }
  }, [isOpen, loadExecutions, loadStats]);

  useEffect(() => {
    if (currentFlowId) {
      setFlowFilter(currentFlowId);
    }
  }, [currentFlowId]);

  const toggleExecution = (execId: string) => {
    if (selectedExec === execId) {
      setSelectedExec(null);
      setExecLogs([]);
    } else {
      setSelectedExec(execId);
      loadLogs(execId);
    }
  };

  const retryExecution = async (execId: string) => {
    try {
      await fetch(`${API_URL}/executions/${execId}/retry`, { method: 'POST' });
      loadExecutions();
    } catch (e) {
      console.error('Error retrying:', e);
    }
  };

  const deleteExecution = async (execId: string) => {
    if (!confirm('Eliminare questa esecuzione?')) return;
    try {
      await fetch(`${API_URL}/executions/${execId}`, { method: 'DELETE' });
      loadExecutions();
    } catch (e) {
      console.error('Error deleting:', e);
    }
  };

  const exportData = async () => {
    try {
      const res = await fetch(`${API_URL}/executions/export/json?${userId ? `owner_id=${userId}` : ''}`);
      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `executions-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
      }
    } catch (e) {
      console.error('Error exporting:', e);
    }
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}min`;
  };

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleString('it-IT', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'failed': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'running': return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'pending': return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'cancelled': return <XCircle className="w-5 h-5 text-gray-500" />;
      case 'timeout': return <Clock className="w-5 h-5 text-orange-500" />;
      default: return <Activity className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case 'success': return 'bg-green-50 border-green-200';
      case 'failed': return 'bg-red-50 border-red-200';
      case 'running': return 'bg-blue-50 border-blue-200';
      case 'pending': return 'bg-yellow-50 border-yellow-200';
      case 'cancelled': return 'bg-gray-50 border-gray-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const getTriggerIcon = (trigger: string) => {
    switch (trigger) {
      case 'schedule': return '⏰';
      case 'webhook': return '🔗';
      case 'api': return '🔌';
      case 'retry': return '🔄';
      default: return '▶️';
    }
  };

  const filteredExecutions = executions.filter(e => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return e.flow_name.toLowerCase().includes(q) || e.id.includes(q);
    }
    return true;
  });

  const uniqueFlows = [...new Set(executions.map(e => e.flow_id))];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-[1100px] max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-500 to-purple-500 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <Activity className="w-6 h-6" />
            <h2 className="text-xl font-bold">Execution History</h2>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={exportData}
              className="text-white hover:bg-white/20 p-2 rounded-lg"
              title="Esporta dati"
            >
              <Download className="w-5 h-5" />
            </button>
            <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('list')}
            className={`px-6 py-3 font-medium ${activeTab === 'list' ? 'text-violet-600 border-b-2 border-violet-500' : 'text-gray-500'}`}
          >
            📜 Lista Esecuzioni
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`px-6 py-3 font-medium ${activeTab === 'stats' ? 'text-violet-600 border-b-2 border-violet-500' : 'text-gray-500'}`}
          >
            <BarChart3 className="w-4 h-4 inline mr-1" />
            Statistiche
          </button>
          <div className="flex-1" />
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 flex items-center gap-1 ${showFilters ? 'text-violet-600' : 'text-gray-500'}`}
          >
            <Filter className="w-4 h-4" />
            Filtri
          </button>
          <button 
            onClick={() => { loadExecutions(); loadStats(); }}
            className="px-4 text-gray-500 hover:text-violet-600"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="p-4 bg-gray-50 border-b flex flex-wrap gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Cerca flow..."
                className="pl-9 pr-4 py-2 border rounded-lg w-48"
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="all">Tutti gli stati</option>
              <option value="success">✅ Success</option>
              <option value="failed">❌ Failed</option>
              <option value="running">🔄 Running</option>
              <option value="pending">⏳ Pending</option>
            </select>
            <select
              value={triggerFilter}
              onChange={e => setTriggerFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="all">Tutti i trigger</option>
              <option value="manual">▶️ Manual</option>
              <option value="schedule">⏰ Schedule</option>
              <option value="webhook">🔗 Webhook</option>
              <option value="api">🔌 API</option>
            </select>
            <select
              value={flowFilter}
              onChange={e => setFlowFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="all">Tutti i flow</option>
              {uniqueFlows.map(fid => (
                <option key={fid} value={fid}>{fid}</option>
              ))}
            </select>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {/* List Tab */}
          {activeTab === 'list' && (
            <div className="p-4 space-y-2">
              {filteredExecutions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nessuna esecuzione trovata</p>
                </div>
              ) : (
                filteredExecutions.map(exec => (
                  <div key={exec.id}>
                    <div 
                      onClick={() => toggleExecution(exec.id)}
                      className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${getStatusBg(exec.status)}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {selectedExec === exec.id ? (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          )}
                          {getStatusIcon(exec.status)}
                          <div>
                            <div className="font-bold">{exec.flow_name}</div>
                            <div className="text-xs text-gray-500">
                              {getTriggerIcon(exec.trigger_type)} {exec.trigger_type} • 
                              ID: {exec.id}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="font-mono text-sm">{formatDuration(exec.duration_ms)}</div>
                            <div className="text-xs text-gray-500">{formatDate(exec.started_at)}</div>
                          </div>
                          <div className="text-sm text-gray-500">
                            {exec.nodes_executed} nodi
                          </div>
                          <div className="flex gap-1">
                            {(exec.status === 'failed' || exec.status === 'cancelled') && (
                              <button
                                onClick={(e) => { e.stopPropagation(); retryExecution(exec.id); }}
                                className="p-2 rounded bg-blue-100 text-blue-600 hover:bg-blue-200"
                                title="Riprova"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteExecution(exec.id); }}
                              className="p-2 rounded bg-red-100 text-red-600 hover:bg-red-200"
                              title="Elimina"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                      {exec.error && (
                        <div className="mt-2 p-2 bg-red-100 text-red-700 rounded text-sm">
                          ❌ {exec.error}
                        </div>
                      )}
                    </div>
                    
                    {/* Logs */}
                    {selectedExec === exec.id && (
                      <div className="ml-8 mt-2 bg-gray-900 rounded-lg p-4 text-white font-mono text-sm max-h-64 overflow-auto">
                        {execLogs.length === 0 ? (
                          <div className="text-gray-500 italic">Nessun log disponibile</div>
                        ) : (
                          execLogs.map((log, i) => (
                            <div key={i} className="mb-1">
                              <span className="text-gray-500">{new Date(log.timestamp).toLocaleTimeString()}</span>
                              <span className={`ml-2 px-1 rounded text-xs ${
                                log.level === 'error' ? 'bg-red-600' :
                                log.level === 'warn' ? 'bg-yellow-600' :
                                'bg-blue-600'
                              }`}>
                                {log.level.toUpperCase()}
                              </span>
                              <span className="ml-2 text-cyan-400">[{log.node_type}]</span>
                              <span className="ml-2 text-white">{log.message}</span>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Stats Tab */}
          {activeTab === 'stats' && stats && (
            <div className="p-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-violet-50 to-purple-100 rounded-xl p-4">
                  <div className="text-3xl font-bold text-violet-600">{stats.total_executions}</div>
                  <div className="text-sm text-gray-600">Esecuzioni totali</div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl p-4">
                  <div className="text-3xl font-bold text-green-600">{stats.success_rate.toFixed(1)}%</div>
                  <div className="text-sm text-gray-600">Tasso successo</div>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-cyan-100 rounded-xl p-4">
                  <div className="text-3xl font-bold text-blue-600">{formatDuration(stats.avg_duration_ms)}</div>
                  <div className="text-sm text-gray-600">Durata media</div>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-rose-100 rounded-xl p-4">
                  <div className="text-3xl font-bold text-red-600">{stats.failed_count}</div>
                  <div className="text-sm text-gray-600">Fallimenti</div>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-2 gap-6">
                {/* By Day Chart */}
                <div className="bg-white border rounded-xl p-4">
                  <h3 className="font-bold mb-4">📅 Ultimi 7 giorni</h3>
                  <div className="space-y-2">
                    {Object.entries(stats.by_day).slice(-7).map(([day, data]) => (
                      <div key={day} className="flex items-center gap-2">
                        <span className="w-20 text-sm text-gray-600">{day.slice(5)}</span>
                        <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden flex">
                          <div 
                            className="bg-green-500 h-full"
                            style={{ width: `${data.total ? (data.success / data.total * 100) : 0}%` }}
                          />
                          <div 
                            className="bg-red-500 h-full"
                            style={{ width: `${data.total ? (data.failed / data.total * 100) : 0}%` }}
                          />
                        </div>
                        <span className="w-10 text-sm text-gray-600 text-right">{data.total}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* By Trigger */}
                <div className="bg-white border rounded-xl p-4">
                  <h3 className="font-bold mb-4">🎯 Per tipo trigger</h3>
                  <div className="space-y-3">
                    {Object.entries(stats.by_trigger).map(([trigger, count]) => (
                      <div key={trigger} className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <span>{getTriggerIcon(trigger)}</span>
                          <span className="capitalize">{trigger}</span>
                        </span>
                        <span className="font-bold">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top Flows */}
                <div className="bg-white border rounded-xl p-4 col-span-2">
                  <h3 className="font-bold mb-4">🏆 Top Flow per esecuzioni</h3>
                  <div className="grid grid-cols-5 gap-4">
                    {stats.top_flows.slice(0, 5).map((flow, i) => (
                      <div key={flow.flow_id} className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-3xl mb-1">{['🥇', '🥈', '🥉', '4️⃣', '5️⃣'][i]}</div>
                        <div className="font-bold text-lg">{flow.count}</div>
                        <div className="text-xs text-gray-600 truncate">{flow.name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

